/**
 * Agent Learning Summary Dashboard Page
 * 
 * Displays AI agent performance metrics and learning trends
 * Shows automated classification success rates and exception handling statistics
 * 
 * Features:
 * - KPI cards showing auto-classification rate, routed contracts, and avoided manual reviews
 * - Sparkline chart visualization of exception trends over time
 * - Confidence threshold display (read-only)
 * - Educational explanation of agent workflow and continuous learning
 * 
 * Metrics Displayed:
 * - Auto Classified Rate: Percentage of contracts classified without human review
 * - Contracts Routed for Review: Low-confidence cases requiring manual verification
 * - Human Touches Avoided: Automated decisions that prevented manual review
 * - Exception Trends: Downward trend showing improvement over time
 * 
 * Data Flow:
 * - Metrics are hardcoded (mock data for UI demonstration)
 * - In production, these would be fetched from an analytics API
 * - Trends show exceptions decreasing as AI confidence improves
 * 
 * Dependencies:
 * - lucide-react: Icons (CheckCircle2, AlertCircle, TrendingDown, Brain, RefreshCw)
 * - Bootstrap 5: CSS grid and component styling
 * 
 * @component
 * @returns {JSX.Element} Dashboard displaying agent performance metrics and trends
 * 
 * @example
 * // Usage in router
 * <Route path="/agent-learning-summary" element={<AgentLearningSummary />} />
 */

import { CheckCircle2, AlertCircle, TrendingDown, Brain, RefreshCw } from "lucide-react";
import HeaderLogo  from "../structure/headerlogo";

/* ========================================================================================
   KPI CARD COMPONENT - Key Performance Indicator Display Card
   ======================================================================================== */

/**
 * KPICard Component
 * 
 * Displays a single key performance indicator with icon, value, and description
 * Used to showcase important metrics in a visually consistent card format
 * 
 * Features:
 * - Icon badge in top-right corner
 * - Large value display (title)
 * - Small label above value
 * - Optional subtitle for additional context
 * - Responsive Bootstrap card layout
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - KPI title/label (e.g., "Auto Classified Rate")
 * @param {string|number} props.value - Main KPI value to display (e.g., "94.2%")
 * @param {string} [props.subtitle] - Optional subtitle with context (e.g., "Contracts classified without review")
 * @param {React.ComponentType} props.icon - Lucide icon component to display in badge
 * @returns {JSX.Element} Card displaying the KPI with icon and values
 * 
 * @example
 * <KPICard
 *   title="Auto Classified Rate"
 *   value="94.2%"
 *   subtitle="Contracts classified without review"
 *   icon={CheckCircle2}
 * />
 */
const KPICard = ({ title, value, subtitle, icon: Icon }) => {
  return (
    <div className="card p-4 shadow-sm border-0">
      {/* Card layout: flex row with label/value on left, icon badge on right */}
      <div className="d-flex justify-content-between">
        <div className="flex-grow-1">
          {/* KPI label/title - small grey text above main value */}
          <p className="text-muted small mb-1">{title}</p>
          {/* Main KPI value - large, bold text */}
          <h3 className="fw-bold mb-1">{value}</h3>
          {/* Optional subtitle - additional context below value */}
          {subtitle && <p className="text-muted small">{subtitle}</p>}
        </div>

        {/* Icon badge - right-aligned circle with icon */}
        <div className="ms-3 p-3 rounded bg-light border">
          <Icon size={20} className="text-primary" />
        </div>
      </div>
    </div>
  );
};

/* ========================================================================================
   SPARKLINE COMPONENT - Inline Trend Chart Visualization
   ======================================================================================== */

/**
 * Sparkline Component
 * 
 * Renders a compact SVG line chart showing trend data over time
 * Useful for showing performance trends without axis labels
 * Automatically scales to fit data range (min to max)
 * 
 * Features:
 * - SVG-based rendering for lightweight charts
 * - Automatic normalization to 0-100 scale
 * - Responsive sizing (fills container width)
 * - Fixed height of 80px for compact display
 * - Label displayed below chart
 * 
 * Algorithm:
 * 1. Find max and min values in dataset
 * 2. Calculate range (max - min)
 * 3. Map each data point to SVG coordinates:
 *    - X-axis: Linear distribution across 0-100 (left to right)
 *    - Y-axis: Normalized value (0-100) inverted for display (top to bottom)
 * 4. Connect points with polyline for continuous trend line
 * 
 * @component
 * @param {Object} props - Component props
 * @param {number[]} props.data - Array of numeric values representing trend data
 * @param {string} props.label - Label to display below the chart
 * @returns {JSX.Element} SVG sparkline chart with label
 * 
 * @example
 * // Show exception trend decreasing over 12 months
 * <Sparkline
 *   data={[45, 42, 38, 35, 33, 30, 28, 26, 25, 24, 23, 23]}
 *   label="Exceptions trending down as Agent improves"
 * />
 */
const Sparkline = ({ data, label }) => {
  // Find min/max values for normalization
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min;

  // Convert data points to SVG polyline coordinates
  // Maps data values to x,y positions where (0,0) is top-left
  const points = data
    .map((value, index) => {
      // X position: distribute evenly across 0-100% width
      const x = (index / (data.length - 1)) * 100;
      // Y position: normalize value to 0-100 range, then invert (100 = bottom, 0 = top)
      // Inversion needed because SVG y-axis increases downward
      const y = 100 - ((value - min) / range) * 100;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <div className="mt-3">
      {/* SVG container - responsive width, fixed height */}
      <div style={{ height: "80px", width: "100%" }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
          {/* Polyline connecting all data points */}
          <polyline
            points={points}
            fill="none"
            stroke="#0d6efd"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      {/* Label below chart */}
      <p className="text-muted small text-center mt-2">{label}</p>
    </div>
  );
};

/* ========================================================================================
   MAIN COMPONENT - Agent Learning Summary Dashboard
   ======================================================================================== */

/**
 * AgentLearningSummary Component
 * 
 * Renders dashboard showing AI agent learning progress and performance metrics
 * Displays KPIs, exception trends, and explanation of agent capabilities
 * 
 * Key Metrics:
 * - autoClassifiedRate (94.2%): Percentage of contracts auto-classified without review
 * - contractsRoutedForReview (23): Number of contracts flagged for human review
 * - humanTouchesAvoided (387): Count of automated decisions avoiding manual review
 * - confidenceThreshold (0.9): Minimum confidence score for auto-classification
 * 
 * Data Sources:
 * - Currently uses hardcoded mock data for UI demonstration
 * - In production, would fetch from analytics API or backend metrics service
 * 
 * @function AgentLearningSummary
 * @returns {JSX.Element} Dashboard page with metrics, trends, and educational content
 */
const AgentLearningSummary = () => {
  // ===== METRIC VALUES (Mock Data) =====
  // These represent AI agent performance metrics
  // In production, these would come from a metrics/analytics API
  
  const autoClassifiedRate = 94.2; // Percentage of contracts classified without human review
  const contractsRoutedForReview = 23; // Number of cases flagged as low-confidence
  const humanTouchesAvoided = 387; // Count of automation successes avoiding manual work
  const confidenceThreshold = 0.9; // Minimum confidence score required for auto-classification

  // ===== TREND DATA =====
  // Exception count over 12 time periods (e.g., months)
  // Downward trend indicates agent learning and improvement
  const exceptionsTrend = [45, 42, 38, 35, 33, 30, 28, 26, 25, 24, 23, 23];

  return (
    <div className="main-content" style={{ paddingTop: 64 }}>
       <HeaderLogo />
      <div className="page-content">
        <div className="container-fluid">
          {/* Page header */}
          <div id="table_height">
            <div className="flex-grow-1" id="row_heading">
              {/* Page title */}
              <div className="page-title">Agent Learning Summary</div>
              {/* Page description */}
              <p className="text-muted my-2 ff-secondary">
                Automated classification performance and continuous improvement metrics              </p>
            </div>

              {/* Logo badge */}
              <div className="ms-auto bg-primary" style={{padding:"1px",border:"1px solid #000",position:"absolute", right:"5px",borderRadius:"2px",top:"15px"}}>
    <img src="../images/predatica_logo.png" alt="Logo" height={"20px"}  /> 
    </div>
    
            {/* Error message container */}
            <div id="app_err"></div>
          </div>
          
          {/* Main content - light grey background */}
    <div className="min-vh-100 bg-light py-4">
      <div className="container">

        {/* ===== KPI CARDS SECTION ===== */}
        {/* Three-column responsive grid showing key performance indicators */}
        <div className="row g-4">
          {/* KPI 1: Auto Classification Rate */}
          <div className="col-12 col-md-4">
            <KPICard
              title="Auto Classified Rate"
              value={`${autoClassifiedRate}%`}
              subtitle="Contracts classified without review"
              icon={CheckCircle2}
            />
          </div>

          {/* KPI 2: Contracts Routed for Review */}
          <div className="col-12 col-md-4">
            <KPICard
              title="Contracts Routed for Review"
              value={contractsRoutedForReview}
              subtitle="Low confidence cases this period"
              icon={AlertCircle}
            />
          </div>

          {/* KPI 3: Human Touches Avoided */}
          <div className="col-12 col-md-4">
            <KPICard
              title="Human Touches Avoided"
              value={humanTouchesAvoided}
              subtitle="Manual reviews not required"
              icon={TrendingDown}
            />
          </div>
        </div>

        {/* ===== EXCEPTION TRENDS SECTION ===== */}
        {/* Card showing trend chart of exception count over time */}
        <div className="card p-4 mt-4 shadow-sm border-0">
          {/* Section header with Brain icon */}
          <div className="d-flex align-items-center gap-2 mb-3">
            <Brain size={20} className="text-primary" />
            <h2 className="fs-5 fw-semibold m-0">Exception Trends</h2>
          </div>

          {/* Sparkline chart showing downward trend */}
          <Sparkline
            data={exceptionsTrend}
            label="Exceptions trending down as Agent improves"
          />
        </div>

        {/* ===== CONFIDENCE THRESHOLD SECTION ===== */}
        {/* Read-only display of current review threshold */}
        <div className="card p-4 mt-4 bg-light border">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              {/* Label for threshold */}
              <p className="text-muted small mb-1">Confidence Threshold</p>
              {/* Current threshold value */}
              <h4 className="fw-semibold mb-0">
                Current review threshold: {confidenceThreshold}
              </h4>
            </div>

            {/* Read-only badge indicating this cannot be modified */}
            <span className="badge bg-secondary">Read Only</span>
          </div>
        </div>

        {/* ===== EXPLANATION SECTION ===== */}
        {/* Educational content about how the agent works */}
        <div className="card p-4 mt-4 border-start border-4 border-info shadow-sm">
          {/* Section title */}
          <h3 className="fs-5 fw-semibold">How the Agent Works</h3>

          {/* Main explanation paragraph */}
          <p className="mt-2">
            The Agent auto-classifies Product Categories, Sales Channels and Territories at L3.
            Only low confidence cases are routed for Human in the Loop review.
            Confirmed corrections teach the Agent and reduce future exceptions.
          </p>

          {/* Feature list with icons */}
          <div className="d-flex flex-wrap gap-3 mt-3">

            {/* Learning feature indicator */}
            <div className="d-flex align-items-center text-muted small">
              <RefreshCw size={16} className="text-success me-2" />
              Learning from corrections
            </div>

            {/* Improvement indicator */}
            <div className="d-flex align-items-center text-muted small">
              <TrendingDown size={16} className="text-success me-2" />
              Reduction in exception workload over time
            </div>

          </div>
        </div>
      </div>
    </div></div></div></div>
  );
};

/**
 * Export Default: AgentLearningSummary Component
 * 
 * Enables default import syntax:
 * import AgentLearningSummary from './pages/agentlearningsummary'
 */
export default AgentLearningSummary;